import React, { useState, useMemo } from 'react';

import { Button, ControlMenu } from 'components';
import { H3, Icon } from 'design-system-web';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { queryGetVMContent } from 'lane-shared/graphql/channel';
import { ContentType } from 'lane-shared/types/content/Content';
import { ContentTypeEnum } from 'constants-content';
import { useQueryString } from 'hooks';
import TabStrip, { TabItem } from 'components/general/TabStrip';
import { AdminPage } from 'components/layout';
import { VisitorManagementUrlSearchParams } from 'components/lds/table/types';
import { PastVisitsComponent } from './tabs/past-visits';
import { VisitorScheduledComponent } from './tabs/scheduled';
import { PendingApprovalComponent } from './tabs/pending-approval';
import styles from './styles.scss';
import { UserType } from 'lane-shared/types/User';
import { VisitCreatePanel } from '../../components/VisitCreatePanel/VisitCreatePanel';
import { Channel } from 'lane-shared/types/ChannelType';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { getSubmissionVisitors } from 'graphql-queries';
import { convertToUUID } from 'uuid-encoding';
import { ApprovalStatus } from 'graphql-query-contracts';
import { Alert, AlertType } from 'components/lds';

const defaultTab = 'scheduled';

interface TabDefinition {
  label: string;
  value: string;
  Component: React.ComponentType<{
    user: UserType;
    channel: Channel;
    searchParams: VisitorManagementUrlSearchParams;
    updateSearchParams: (prop: Record<string, unknown>) => void;
  }>;
  alert?: number;
}

const BaseTabsMap: Record<string, TabDefinition> = {
  scheduled: {
    label: 'web.admin.channel.visitor.log.tab.scheduled.visits',
    value: 'scheduled',
    Component: VisitorScheduledComponent,
  },
  pastVisits: {
    // FIXME: translation already done in TabStrip component
    // sending translated label here is then again looked for key in translation again - packages/localization/locales/en-US/translation.json
    label: 'web.admin.channel.visitor.log.tab.past.visits',
    value: 'pastVisits',
    Component: PastVisitsComponent,
  },
  pendingApproval: {
    label: 'web.admin.channel.visitor.log.tab.pending.approval',
    value: 'pendingApproval',
    Component: PendingApprovalComponent,
  },
};

export function VisitorLogPage({
  user,
  channel,
}: {
  user: UserType;
  channel: Channel;
}) {
  const { t } = useTranslation();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  const isWatchlistV2Enabled = useFlag(
    FeatureFlag.VisitorManagementWatchlistV2,
    false
  );

  const organizationId = channel?._id ? convertToUUID(channel._id) : '';

  const { data: content } = useQuery(queryGetVMContent, {
    variables: {
      channelId: channel?._id,
      search: {
        type: {
          any: ContentTypeEnum.VisitorManagement,
        },
      },
    },
  });

  // Query for pending approval count to show in tab badge
  // TODO: This query should be replaced with a query that gets the only pending approval count
  const { data: pendingData } = useQuery(getSubmissionVisitors, {
    variables: {
      filter: {
        organizationId,
        approvalStatus: ApprovalStatus.ApprovalStatusPending,
      },
    },
    skip: !isWatchlistV2Enabled || !organizationId,
    fetchPolicy: 'cache-and-network',
  });

  const pendingApprovalCount = pendingData?.submissionVisitors?.totalCount || 0;

  const visitorForms: ContentType[] | undefined = content?.getVMContent?.items;
  const [query, goToUrl] = useQueryString<
    VisitorManagementUrlSearchParams<string>
  >({
    tab: defaultTab,
  });

  const showPendingApprovalBanner =
    isWatchlistV2Enabled && pendingApprovalCount > 0 && !isBannerDismissed;

  const handleReviewNowClick = () => {
    setIsBannerDismissed(true);
    goToUrl({ tab: 'pendingApproval' });
  };

  // Build tabs dynamically based on feature flags
  const tabs: TabItem[] = useMemo(() => {
    const baseTabs: TabItem[] = [
      {
        label: BaseTabsMap.scheduled.label,
        value: BaseTabsMap.scheduled.value,
      },
      {
        label: BaseTabsMap.pastVisits.label,
        value: BaseTabsMap.pastVisits.value,
      },
    ];

    if (isWatchlistV2Enabled) {
      baseTabs.push({
        label: BaseTabsMap.pendingApproval.label,
        value: BaseTabsMap.pendingApproval.value,
        alert: pendingApprovalCount > 0 ? pendingApprovalCount : undefined,
      });
    }

    return baseTabs;
  }, [isWatchlistV2Enabled, pendingApprovalCount]);

  const getTabComponent = (selectedTab: string) => {
    const tabDef = BaseTabsMap[selectedTab];

    return tabDef ? tabDef.Component : BaseTabsMap[defaultTab].Component;
  };

  const CurrentTab = getTabComponent(query.tab);

  const onPanelClose = () => {
    setIsPanelOpen(false);
  };

  return (
    <AdminPage>
      <div className={styles.visitorTitleContainer}>
        <H3 as="h1">{t('web.admin.channel.visitor.log.title')}</H3>
        <Button
          color="inherit"
          variant="contained"
          testId="createNewVisit"
          onClick={() => setIsPanelOpen(true)}
        >
          {t('web.admin.channel.visitor.log.create.new.visit.button')}
        </Button>
      </div>

      {showPendingApprovalBanner && (
        <div className={styles.pendingApprovalBanner}>
          <Alert type={AlertType.warning} fullWidth>
            <Trans
              i18nKey="web.admin.channel.visitor.log.pendingApproval.banner.message"
              values={{ count: pendingApprovalCount }}
              components={{
                strong: <strong className={styles.bannerStrong} />,
                reviewLink: (
                  <button
                    type="button"
                    className={styles.reviewLink}
                    onClick={handleReviewNowClick}
                    aria-label={t('web.admin.channel.visitor.log.pendingApproval.banner.reviewNow')}
                  />
                ),
              }}
            />
          </Alert>
          <button
            type="button"
            className={styles.bannerCloseButton}
            onClick={() => setIsBannerDismissed(true)}
            aria-label={t('web.admin.channel.visitor.log.closeBanner')}
          >
            <Icon name="times" />
          </button>
        </div>
      )}

      <ControlMenu>
        <TabStrip
          tabs={tabs}
          selected={{ value: query.tab }}
          onSelectTab={tab => goToUrl({ tab: tab.value })}
          className={styles.visitorTabs}
        />
      </ControlMenu>
      <CurrentTab
        user={user}
        channel={channel}
        searchParams={query}
        updateSearchParams={goToUrl}
      />
      <VisitCreatePanel
        isPanelOpen={isPanelOpen}
        onPanelClose={onPanelClose}
        visitorForms={visitorForms!}
      />
    </AdminPage>
  );
}
